import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg } from '../lib/api';
import { Avatar, ProgressRing, CheerList, CheerComposer } from './SocialBits';

export function SocialVaultDialog({ goalId, onClose }) {
  const [v, setV] = useState(null);
  const load = () => api.get(`/social/vaults/${goalId}`).then((r) => setV(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  useEffect(() => { if (goalId) load(); }, [goalId]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleFollow = async () => {
    try { v.is_following ? await api.delete(`/social/vaults/${goalId}/follow`) : await api.post(`/social/vaults/${goalId}/follow`); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={!!goalId} onOpenChange={onClose}>
      <DialogContent className="glass-modal sm:max-w-md" data-testid="social-vault-dialog">
        {!v ? <Loader2 className="h-6 w-6 animate-spin text-[#9333EA] mx-auto my-8" /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white flex items-center gap-2" data-testid="social-vault-name">{v.name}</DialogTitle>
              <DialogDescription className="text-[#A19DB0] flex items-center gap-2"><Avatar user={v.owner} size={5} />{v.owner.name}'s vault</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-4 py-2">
              <ProgressRing percent={v.progress_percent} size={72} />
              <div className="text-sm">
                <p className="text-white font-semibold" data-testid="social-vault-days-left">{v.days_left === 0 ? 'Unlocks today' : `${v.days_left} days to unlock`}</p>
                <p className="text-[#A19DB0] text-xs">{v.follower_count} following · {v.cheers.length} cheers</p>
              </div>
              {v.access === 'friend' && (
                <button data-testid="social-follow-toggle-btn" onClick={toggleFollow} className={`ml-auto px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${v.is_following ? 'border-white/20 text-[#A19DB0] hover:text-white' : 'bg-[#9333EA] border-[#9333EA] text-white hover:bg-[#7E22CE]'}`}>
                  {v.is_following ? 'Following' : 'Follow'}
                </button>
              )}
            </div>
            <CheerComposer goalId={goalId} onSent={load} />
            {v.cheers.length > 0 && <div className="max-h-48 overflow-y-auto pt-2"><CheerList cheers={v.cheers} /></div>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function GroupContributeDialog({ group, onClose }) {
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const a = parseFloat(amount);
    if (!a || a < 5) { setQuote(null); return; }
    const t = setTimeout(() => api.post('/connect/deposit/quote', { amount: a }).then((r) => setQuote(r.data)).catch(() => setQuote(null)), 300);
    return () => clearTimeout(t);
  }, [amount]);
  const pay = async () => {
    setBusy(true);
    try {
      const r = await api.post('/connect/deposit', { goal_id: group.goal_id, amount: parseFloat(amount), origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) { toast.error(errMsg(err, 'Payment failed. Try again.')); setBusy(false); }
  };
  return (
    <Dialog open={!!group} onOpenChange={onClose}>
      <DialogContent className="glass-modal sm:max-w-md" data-testid="group-contribute-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white">Add to {group?.name}</DialogTitle>
          <DialogDescription className="text-[#A19DB0]">Your money goes into {group?.owner?.name}'s vault. Only the owner can withdraw it on the unlock date.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#A19DB0]" />
          <Input data-testid="group-contribute-amount-input" type="number" min="5" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-12 input-dark h-14 text-lg font-mono" placeholder="5.00 minimum" />
        </div>
        {quote && <p className="text-xs text-[#A19DB0]" data-testid="group-contribute-total">Card charge ${Number(quote.total_charge).toFixed(2)} (includes fees) · ${Number(quote.deposit_amount).toFixed(2)} goes into the vault</p>}
        <button data-testid="group-contribute-pay-btn" disabled={busy || !quote} onClick={pay} className="w-full h-12 rounded-xl btn-primary font-semibold disabled:opacity-50">{busy ? 'Opening checkout…' : 'Continue to payment'}</button>
      </DialogContent>
    </Dialog>
  );
}
